<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: Which payees took the window's money, and by how much against the window before?
  Answer:   the expense total with its change.
  Context:  each payee's share and entry count.
  Controls: the window, bucket and tab strip in the frame.
-->
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  type BreakdownRow,
  canViewReporting,
  ExpenseBreakdownView,
  groupKeyOf,
  groupLabelOf,
  ReportFrame,
  useExpenseBreakdownQuery,
  useReportScope,
} from '@/features/reporting'

const { t } = useI18n()
const scope = useReportScope(() => canViewReporting(scope.session.can))
const { session, state } = scope
const breakdown = useExpenseBreakdownQuery({ ...scope.flows(), groupBy: () => 'PAYEE' })
const windowDays = computed(() => state.range.value.until.epochDay - state.range.value.from.epochDay + 1)

const rows = computed<BreakdownRow[]>(() =>
  (breakdown.data.value?.items ?? []).map((item) => ({
    key: groupKeyOf(item.group),
    label: groupLabelOf(item.group, {
      accountLabel: () => undefined,
      noPayee: t('reporting.noPayee'),
      untagged: t('reporting.untagged'),
      unknownAccount: t('reporting.unknownAccount'),
    }),
    icon: undefined,
    entryCount: item.entryCount,
    amount: item.amount,
    share: item.share,
  })),
)
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')" :state="state">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>
    <ExpenseBreakdownView
      v-else-if="breakdown.data.value"
      :rows="rows"
      :total="breakdown.data.value.total"
      :previous-total="breakdown.data.value.previousTotal"
      :window-days="windowDays"
      :caption="t('reporting.tabs.payees')"
    />
  </ReportFrame>
</template>
